import { LuIcon, Menu } from "@overmap-ai/blocks"
import { type CheckboxListField, fieldDataMapping, normalizeFieldOptions } from "@overmap-ai/forms"
import { use, useCallback, useMemo } from "react"

import { SelectFieldOptionMultiSelectGroup } from "../../../components/MenuMultiSelectGroups/SelectFieldOptionMultiSelectGroup"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { CheckboxListFieldFilterDef } from "./CheckboxListFieldFilterDef"

interface CheckboxListFieldFilterCellProps extends FilterDefRenderProps<CheckboxListFieldFilterDef> {
	field: CheckboxListField
}

export const CheckboxListFieldFilterCell = (props: CheckboxListFieldFilterCellProps) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const normalizedOptions = useMemo(() => normalizeFieldOptions(field.options), [field.options])

	const optionValueLabelMapping = useMemo(() => {
		const mapping: Record<string, string> = {}

		for (const option of normalizedOptions) {
			mapping[option.value] = option.label
		}

		return mapping
	}, [normalizedOptions])

	const handleValuesChange = useCallback(
		(values: string[]) => {
			if (values.length > 0) {
				filter.setFilterValue(values)
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const handleSelectEmpty = useCallback(() => {
		filter.setFilterValue(null)
	}, [filter])

	const getFilterValueRender = () => {
		if (filterValue === undefined) return "..."

		if (filterValue === null) return "empty"

		switch (filterValue.length) {
			case 0:
				return "..."
			case 1: {
				const value = filterValue[0]
				return optionValueLabelMapping[value] ?? value
			}
			default:
				return `${filterValue.length} options`
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge className="shrink-1 min-w-0">
				<LuIcon icon={fieldDataMapping[field.type].icon} />
				<span className="truncate">{field.label}</span>
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			<Menu.Root>
				<Menu.ClickTrigger>
					<FilterCellButton className="truncate">{getFilterValueRender()}</FilterCellButton>
				</Menu.ClickTrigger>
				<Menu.Content size={size}>
					<Menu.Scroll>
						<SelectFieldOptionMultiSelectGroup
							values={filterValue ?? []}
							selectFieldOptions={field.options}
							onValuesChange={handleValuesChange}
						/>
						<Menu.Separator />
						<Menu.Item onSelect={handleSelectEmpty}>empty</Menu.Item>
					</Menu.Scroll>
				</Menu.Content>
			</Menu.Root>

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
